import axiosInstance from '@/setup/axios/axiosInstance';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { InventoryPurchaseOrderFormData, InventoryPurchaseOrderRow, PurchaseOrderLine } from '../types';

interface ApiPurchaseLine {
  id?: string;
  sort_order?: number;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  uom_id?: string;
  uom_name?: string;
  requested_qty?: string | number | null;
  ordered_qty?: string | number | null;
  received_qty?: string | number | null;
  remaining_qty?: string | number | null;
  unit_price?: string | number | null;
  line_amount?: string | number | null;
  manufacturer_id?: string | null;
  manufacturer_name?: string | null;
  source_request_line_id?: string | null;
  note?: string;
}

interface ApiPurchase {
  id: string;
  po_no?: string;
  purchase_type?: string;
  purchase_type_display?: string;
  status: string;
  status_code?: string;
  status_display?: string;
  status_color?: string;
  company_id?: string;
  company_name?: string;
  supplier_id?: string;
  supplier_name?: string;
  warehouse_id?: string;
  warehouse_name?: string;
  order_id?: string | null;
  order_no?: string | null;
  material_request_id?: string | null;
  material_request_no?: string | null;
  requested_by_id?: string | null;
  requested_by_name?: string | null;
  approved_by_id?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
  note?: string;
  lines?: ApiPurchaseLine[];
  [key: string]: any;
}

interface ApiListResponse {
  results?: ApiPurchase[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const resolvePurchaseType = (purchase: ApiPurchase): string => (
  purchase.order_id || purchase.material_request_id || purchase.purchase_type === 'order'
    ? 'order'
    : 'reserve'
);

export interface GetPurchaseOrdersParams {
  companyId?: string;
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

const mapApiLine = (l: ApiPurchaseLine, index: number): PurchaseOrderLine => ({
  id: l.id || `line-${index}`,
  sort_order: l.sort_order ?? index + 1,
  item_id: l.item_id || '',
  item_code: l.item_code || '',
  item_name: l.item_name || '',
  uom_id: l.uom_id || '',
  uom_name: l.uom_name || '',
  requested_qty: String(l.requested_qty ?? '0'),
  ordered_qty: String(l.ordered_qty ?? '0'),
  received_qty: String(l.received_qty ?? '0'),
  remaining_qty: String(l.remaining_qty ?? '0'),
  unit_price: String((l as any).unit_price ?? (l as any).unitPrice ?? (l as any).price ?? '0'),
  line_amount: String((l as any).line_amount ?? (l as any).lineAmount ?? (l as any).amount ?? '0'),
  manufacturer_id: l.manufacturer_id || null,
  manufacturer_name: l.manufacturer_name || null,
  source_request_line_id: l.source_request_line_id || null,
  note: l.note || '',
});

const mapApiToRow = (r: ApiPurchase): InventoryPurchaseOrderRow => {
  const amount = (r.lines || []).reduce((acc, l) => {
    const v = typeof l.line_amount === 'string' ? parseFloat(l.line_amount || '0') : Number(l.line_amount || 0);
    return acc + (isNaN(v) ? 0 : v);
  }, 0);

  return {
    id: r.id,
    code: r.po_no || '',
    name: r.purchase_type_display || '',
    purchase_type: resolvePurchaseType(r),
    supplier: r.supplier_name || '',
    supplier_id: r.supplier_id || '',
    status: r.status,
    status_code: r.status_code || '',
    status_display: r.status_display || r.status,
    status_color: r.status_color || '',
    amount: amount || 0,
    date: r.created_at || r.updated_at || '',
    company_id: r.company_id || '',
    company_name: r.company_name || '',
    warehouse_id: r.warehouse_id || '',
    warehouse_name: r.warehouse_name || '',
    material_request_id: r.material_request_id || null,
    material_request_no: r.material_request_no || null,
    requested_by_id: r.requested_by_id || null,
    requested_by_name: r.requested_by_name || null,
    note: r.note || '',
    created_by: r.created_by || undefined,
    created_by_name: r.created_by_name || undefined,
    updated_by: r.updated_by || undefined,
    updated_by_name: r.updated_by_name || undefined,
    updated_at: r.updated_at || undefined,
  };
};

const mapApiToFormData = (r: ApiPurchase): InventoryPurchaseOrderFormData => ({
  po_no: r.po_no || '',
  purchase_type: resolvePurchaseType(r),
  status: r.status || '',
  status_code: r.status_code || '',
  status_display: r.status_display || '',
  status_color: r.status_color || '',
  company_id: r.company_id || '',
  company_name: r.company_name || '',
  supplier_id: r.supplier_id || '',
  supplier_name: r.supplier_name || '',
  warehouse_id: r.warehouse_id || '',
  warehouse_name: r.warehouse_name || '',
  order_id: r.order_id || null,
  order_no: r.order_no || null,
  material_request_id: r.material_request_id || null,
  material_request_no: r.material_request_no || null,
  requested_by_id: r.requested_by_id || null,
  requested_by_name: r.requested_by_name || null,
  approved_by_id: r.approved_by_id || null,
  approved_by_name: r.approved_by_name || null,
  approved_at: r.approved_at || null,
  note: r.note || '',
  lines: (r.lines || []).map(mapApiLine),
});

const resolvePurchaseCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'global' || resolved === 'all' || !isUuid(resolved)) {
    return undefined;
  }
  return resolved;
};

export async function getPurchaseOrders(params: GetPurchaseOrdersParams = {}): Promise<{
  results: InventoryPurchaseOrderRow[];
  count: number;
  totalPages: number;
}> {
  const query = new URLSearchParams();
  const companyId = resolvePurchaseCompanyId(params.companyId);
  if (companyId) {
    query.append('company', companyId);
  }
  if (params.search) query.append('search', params.search);
  if (params.status && params.status !== 'all') query.append('status', params.status);
  if (params.page) query.append('page', String(params.page));
  if (params.pageSize) query.append('page_size', String(params.pageSize));

  const qs = query.toString();
  const response = await axiosInstance.get<ApiListResponse | ApiPurchase[]>(`/inventory/purchases/${qs ? `?${qs}` : ''}`);
  const data = response.data;

  if (Array.isArray(data)) {
    return { results: data.map(mapApiToRow), count: data.length, totalPages: 1 };
  }

  const paged = data as ApiListResponse;
  return {
    results: (paged.results || []).map(mapApiToRow),
    count: paged.count || 0,
    totalPages: paged.total_pages || 1,
  };
}

export async function getPurchaseOrder(id: string): Promise<InventoryPurchaseOrderRow> {
  const response = await axiosInstance.get<ApiPurchase>(`/inventory/purchases/${id}/`);
  return mapApiToRow(response.data);
}

export async function getPurchaseOrderDetail(id: string): Promise<InventoryPurchaseOrderFormData> {
  const response = await axiosInstance.get<ApiPurchase>(`/inventory/purchases/${id}/`);
  return mapApiToFormData(response.data);
}

export type PurchaseOrderBulkAction = 'approve' | 'delete';

export interface PurchaseOrderBulkActionResponse {
  total?: number;
  success_count?: number;
  failed_count?: number;
  results?: Array<{
    id: string;
    success: boolean;
    error?: string;
  }>;
  [key: string]: any;
}

export async function bulkPurchaseOrderAction(ids: string[], action: PurchaseOrderBulkAction): Promise<PurchaseOrderBulkActionResponse> {
  const response = await axiosInstance.post<PurchaseOrderBulkActionResponse>('/inventory/purchases/bulk-actions/', {
    ids,
    action,
  });
  return response.data;
}

export async function createPurchaseOrdersForSuppliers(
  orders: Array<{
    supplierName: string;
    supplierId?: string;
    companyId: string;
    warehouseId: string;
    materialRequestId?: string;
    requestedById?: string;
    items: Array<{
      itemId: string;
      uomId: string;
      requestedQty: number;
      orderedQty: number;
      unitPrice: number;
      sourceRequestLineId?: string;
    }>;
  }>,
): Promise<void> {
  await Promise.all(
    orders.map((order) =>
      axiosInstance.post('/inventory/purchases/', {
        purchase_type: order.materialRequestId ? 'order' : 'reserve',
        company_id: order.companyId,
        warehouse_id: order.warehouseId,
        supplier_id: order.supplierId || null,
        material_request_id: order.materialRequestId || null,
        requested_by_id: order.requestedById || null,
        lines: order.items.map((item) => ({
          item_id: item.itemId,
          uom_id: item.uomId,
          requested_qty: item.requestedQty,
          ordered_qty: item.orderedQty,
          unit_price: item.unitPrice,
          source_request_line_id: item.sourceRequestLineId || null,
        })),
      }),
    ),
  );
}

/**
 * Create or update a single purchase order. If `id` is provided, performs PUT to update,
 * otherwise POST to create.
 */
export async function savePurchaseOrder(form: InventoryPurchaseOrderFormData, id?: string): Promise<ApiPurchase> {
  const payload: any = {
    po_no: form.po_no || undefined,
    purchase_type: form.purchase_type,
    status: form.status,
    company_id: form.company_id,
    supplier_id: form.supplier_id || null,
    warehouse_id: form.warehouse_id || null,
    order_id: form.order_id || null,
    material_request_id: form.material_request_id || null,
    requested_by_id: form.requested_by_id || null,
    approved_by_id: form.approved_by_id || null,
    note: form.note || '',
    lines: (form.lines || []).map((l) => ({
      id: l.id && typeof l.id === 'string' ? l.id : undefined,
      sort_order: l.sort_order,
      item_id: l.item_id || null,
      item_code: l.item_code || null,
      item_name: l.item_name || null,
      uom_id: l.uom_id || null,
      uom_name: l.uom_name || null,
      requested_qty: l.requested_qty != null ? l.requested_qty : null,
      ordered_qty: l.ordered_qty != null ? l.ordered_qty : null,
      unit_price: l.unit_price != null ? l.unit_price : null,
      source_request_line_id: l.source_request_line_id || null,
      manufacturer_id: l.manufacturer_id || null,
      note: l.note || '',
    })),
  };

  if (id) {
    const response = await axiosInstance.put<ApiPurchase>(`/inventory/purchases/${id}/`, payload);
    return response.data;
  }

  const response = await axiosInstance.post<ApiPurchase>('/inventory/purchases/', payload);
  return response.data;
}
